import { FC, useState } from 'react'
import {
  Box,
  Select,
  MenuItem,
  Typography,
  FormControl,
  SelectProps,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Checkbox
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { MenuProps, menuItemSx, getFormControlSx, checkboxSx } from './styles/uncontrolledSelect.styles'
import { InstanceCheckedIcon, InstanceUncheckedIcon } from '../svg'

export interface SelectOption {
  label: string
  value: string
}

interface Props extends SelectProps {
  loading?: boolean
  options: SelectOption[]
  withCheckbox?: boolean
  dropdownHeight?: string
}

const UncontrolledSelect: FC<Props> = ({ options, loading, dropdownHeight, withCheckbox = true, ...rest }) => {
  const [isOpen, setIsOpen] = useState(false)
  const isMultiple = rest.multiple
  const selectedValues = rest.value as string | string[]

  const hasAllOption = options.some((opt) => opt.value === 'all')
  const regularOptions = hasAllOption ? options.filter((opt) => opt.value !== 'all') : options

  const getDisplayText = () => {
    if (!selectedValues || (Array.isArray(selectedValues) && selectedValues.length === 0)) {
      return rest.placeholder || 'Select...'
    }

    if (isMultiple && Array.isArray(selectedValues)) {
      const count = selectedValues.length
      const allSelected = hasAllOption ? count === regularOptions.length : count === options.length
      return allSelected ? `All (${count})` : `${count} selected`
    }

    const selected = options.find((option) => option.value === selectedValues)
    return selected?.label || ''
  }

  const isItemSelected = (optionValue: string) => {
    if (!isMultiple) {
      return selectedValues === optionValue
    }

    const selectedArray = selectedValues as string[]

    if (optionValue === 'all') {
      return selectedArray.length === regularOptions.length
    }

    return selectedArray.includes(optionValue)
  }

  return (
    <div>
      <FormControl sx={getFormControlSx(isOpen)}>
        {rest.label && (
          <Typography id="uncontrolled-select-label" className="label">
            {rest.label}
          </Typography>
        )}
        <Select
          labelId="uncontrolled-select-label"
          id="uncontrolled-select-checkbox"
          displayEmpty
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          MenuProps={{
            ...MenuProps,
            PaperProps: {
              ...MenuProps.PaperProps,
              sx: { ...MenuProps.PaperProps.sx, height: dropdownHeight }
            }
          }}
          IconComponent={(props) => (!loading ? <ExpandMoreIcon {...props} /> : null)}
          renderValue={() => getDisplayText()}
          inputProps={{ 'aria-label': 'Without label' }}
          {...rest}
          input={
            <OutlinedInput
              endAdornment={
                loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <CircularProgress size={20} sx={{ color: '#9CA3AF' }} />
                  </Box>
                )
              }
            />
          }
        >
          {options.map((option) => {
            const isSelected = isItemSelected(option.value)

            return (
              <MenuItem key={option.value} value={option.value} sx={menuItemSx}>
                {withCheckbox && (
                  <Checkbox
                    checked={isSelected}
                    sx={checkboxSx}
                    checkedIcon={<InstanceCheckedIcon />}
                    icon={<InstanceUncheckedIcon />}
                  />
                )}
                <ListItemText primary={option.label} />
              </MenuItem>
            )
          })}
        </Select>
      </FormControl>
    </div>
  )
}

export default UncontrolledSelect
